import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { toStages, toEditorStages, fromStages, sanitizeStages, effectiveDeps, newUid } from '../utils/workflowGraph';

const TYPE_AR = { task: 'مهمة', approval: 'اعتماد', fork: 'تفرّع (قديم)' };

const emptyStep = () => ({
  uid: newUid(), name: '', type: 'task', default_assignee: '', approver: '', pay_item_id: '', join: 'all', deps: null,
});
const emptyForm = () => ({ name: '', description: '', stages: [[emptyStep()]], is_active: true, project_id: '' });

/** رسم المسار: كل عمود مرحلة، وما تكدّس داخل العمود يعمل متوازياً */
function PathPreview({ steps }) {
  const stages = toStages(steps);
  if (!stages.length) return null;

  return (
    <div className="wf-path">
      {stages.map((stage, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="wf-arrow">←</span>}
          <div className={`wf-stage${stage.length > 1 ? ' wf-stage-parallel' : ''}`}>
            {stage.length > 1 && <span className="wf-stage-tag">متوازية</span>}
            {stage.map((s) => (
              <span key={s.key} className={`wf-step wf-step-${s.type || 'task'}`}>
                {s.name || 'خطوة'}
                {s.default_assignee && <span className="wf-step-meta">{s.default_assignee.split('@')[0]}</span>}
                {s.type === 'approval' && <span className="wf-step-meta">⏳ اعتماد</span>}
                {(s.depends_on || []).length > 1 && s.join === 'any' && (
                  <span className="wf-step-meta">تبدأ بأوّل متطلب</span>
                )}
              </span>
            ))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Workflows() {
  const { api, user } = useAuth();
  const toast = useToast();
  const isAdmin = ['admin', 'supervisor'].includes(user?.role);
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [payItems, setPayItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => api('/workflows').then(setWorkflows).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    if (isAdmin) {
      api('/users').then(setUsers).catch(() => {});
      api('/pay-items').then(setPayItems).catch(() => {});
      api('/projects').then(setProjects).catch(() => {});
    }
  }, []);

  const openCreate = () => {
    setSelected(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (w) => {
    setSelected(w);
    setForm({
      name: w.name || '',
      description: w.description || '',
      is_active: w.is_active ?? true,
      project_id: w.project_id || '',
      stages: w.steps?.length ? toEditorStages(w.steps) : [[emptyStep()]],
    });
    setShowModal(true);
  };

  const save = async () => {
    const steps = fromStages(form.stages);
    if (!form.name.trim() || steps.some((s) => !s.name)) {
      toast.warning('الرجاء إدخال اسم المسار واسم كل خطوة');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        is_active: form.is_active,
        project_id: form.project_id || null,
        steps,
      };
      if (selected) {
        await api(`/workflows/${selected.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await api('/workflows', { method: 'POST', body: JSON.stringify(payload) });
      }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  const remove = async (w) => {
    if (!window.confirm(`حذف المسار "${w.name}"؟`)) return;
    try {
      await api(`/workflows/${w.id}`, { method: 'DELETE' });
      load();
    } catch (err) { toast.error(err.message); }
  };

  // — تعديل رسم المسار —
  const setStages = (build) => setForm((f) => {
    const stages = sanitizeStages(build(f.stages));
    return { ...f, stages: stages.length ? stages : [[emptyStep()]] };
  });

  const updateStep = (si, k, patch) => setStages((stages) =>
    stages.map((st, i) => (i !== si ? st : st.map((s, j) => (j === k ? { ...s, ...patch } : s)))));

  const addParallelStep = (si) => setStages((stages) =>
    stages.map((st, i) => (i === si ? [...st, emptyStep()] : st)));

  const addStage = () => setStages((stages) => [...stages, [emptyStep()]]);

  const removeStep = (si, k) => setStages((stages) =>
    stages.map((st, i) => (i === si ? st.filter((_, j) => j !== k) : st)));

  const removeStage = (si) => setStages((stages) => stages.filter((_, i) => i !== si));

  const moveStage = (si, dir) => setStages((stages) => {
    const j = si + dir;
    if (j < 0 || j >= stages.length) return stages;
    const next = [...stages];
    [next[si], next[j]] = [next[j], next[si]];
    return next;
  });

  /** نقل خطوة إلى مرحلة مجاورة — النقل بعد الأخيرة ينشئ مرحلة جديدة */
  const moveStepToStage = (si, k, dir) => setStages((stages) => {
    const j = si + dir;
    if (j < 0) return stages;
    const step = stages[si][k];
    const next = stages.map((st, i) => (i === si ? st.filter((_, x) => x !== k) : [...st]));
    if (j >= next.length) next.push([step]);
    else next[j] = [...next[j], step];
    return next;
  });

  if (loading) return <div className="loader">جاري التحميل...</div>;

  const previewSteps = fromStages(form.stages);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>مسارات العمل</h2>
        {isAdmin && <button className="btn btn-primary" data-tour="workflows-create" onClick={openCreate}>+ مسار جديد</button>}
      </div>

      <div style={{ display: 'grid', gap: 16 }}>
        {workflows.map((w) => {
          const stages = toStages(w.steps);
          const parallel = stages.filter((st) => st.length > 1).length;
          return (
            <div key={w.id} className="card">
              <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span className="card-title">{w.name}</span>
                  <span className="badge badge-in_progress">{w.steps?.length || 0} خطوات</span>
                  <span className="badge badge-pending">{stages.length} مراحل</span>
                  {parallel > 0 && <span className="badge badge-info">⑂ {parallel} مرحلة متوازية</span>}
                  {w.project ? (
                    <span className="badge badge-info">📁 {w.project.title}</span>
                  ) : (
                    <span className="badge badge-pending">عام</span>
                  )}
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(w)}>تعديل</button>
                    <button className="btn btn-sm btn-outline" style={{ color: 'var(--c-coral)', borderColor: 'var(--c-coral)' }} onClick={() => remove(w)}>حذف</button>
                  </div>
                )}
              </div>
              {w.description && <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>{w.description}</p>}
              <PathPreview steps={w.steps} />
            </div>
          );
        })}
        {workflows.length === 0 && (
          <div className="card">
            <div className="empty"><div className="empty-icon">🛤️</div>لا توجد مسارات</div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820, maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-title">{selected ? 'تعديل مسار' : 'مسار جديد'}</div>

            <div className="form-group">
              <label className="form-label">اسم المسار *</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">الوصف</label>
              <textarea className="form-textarea" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">المشروع (اختياري)</label>
              <select className="form-select" value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))}>
                <option value="">— مسار عام (لكل المشاريع) —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
              <small style={{ color: 'var(--text-faint)' }}>
                اترك الحقل فارغاً ليكون المسار عاماً متاحاً لكل المشاريع، أو اختر مشروعاً ليكون خاصاً به.
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                <span>نشط</span>
              </label>
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label className="form-label" style={{ margin: 0 }}>رسم المسار</label>
                <button className="btn btn-sm btn-outline" onClick={addStage}>+ مرحلة تالية</button>
              </div>
              <small style={{ color: 'var(--text-faint)', display: 'block', marginBottom: 12 }}>
                المراحل تُنفَّذ بالتتابع، وخطوات المرحلة الواحدة تُفتح معاً وتُنفَّذ متوازية.
              </small>

              <div style={{ marginBottom: 14 }}><PathPreview steps={previewSteps} /></div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {form.stages.map((stage, si) => (
                  <div key={si} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>المرحلة {si + 1}</span>
                        {si === 0 && <span className="badge badge-pending">بداية المسار</span>}
                        {stage.length > 1 && <span className="badge badge-info">⑂ {stage.length} خطوات متوازية</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-sm btn-outline" onClick={() => addParallelStep(si)}>+ خطوة موازية</button>
                        <button className="btn btn-sm btn-outline" onClick={() => moveStage(si, -1)} disabled={si === 0} title="تقديم المرحلة">↑</button>
                        <button className="btn btn-sm btn-outline" onClick={() => moveStage(si, 1)} disabled={si === form.stages.length - 1} title="تأخير المرحلة">↓</button>
                        <button className="btn btn-sm btn-outline" style={{ color: 'var(--c-coral)' }} onClick={() => removeStage(si)} disabled={form.stages.length === 1}>حذف المرحلة</button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: stage.length > 1 ? 'repeat(auto-fit, minmax(260px, 1fr))' : '1fr' }}>
                      {stage.map((step, k) => {
                        const deps = effectiveDeps(form.stages, si, step);
                        const earlier = form.stages.slice(0, si).flat();
                        return (
                          <div key={step.uid} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--surface-alt)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>خطوة {k + 1}</span>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button className="btn btn-sm btn-outline" onClick={() => moveStepToStage(si, k, -1)} disabled={si === 0} title="نقل لمرحلة سابقة">⇡</button>
                                <button className="btn btn-sm btn-outline" onClick={() => moveStepToStage(si, k, 1)} disabled={si === form.stages.length - 1 && stage.length === 1} title="نقل لمرحلة تالية">⇣</button>
                                <button className="btn btn-sm btn-outline" style={{ color: 'var(--c-coral)' }} onClick={() => removeStep(si, k)} disabled={form.stages.length === 1 && stage.length === 1}>حذف</button>
                              </div>
                            </div>

                            <div className="form-group">
                              <label className="form-label">اسم الخطوة *</label>
                              <input className="form-input" value={step.name} onChange={(e) => updateStep(si, k, { name: e.target.value })} />
                            </div>

                            <div className="form-group">
                              <label className="form-label">النوع</label>
                              <select className="form-select" value={step.type} onChange={(e) => updateStep(si, k, { type: e.target.value })}>
                                <option value="task">مهمة</option>
                                <option value="approval">اعتماد</option>
                                {step.type === 'fork' && <option value="fork">{TYPE_AR.fork}</option>}
                              </select>
                            </div>

                            {step.type === 'task' && (
                              <>
                                <div className="form-group">
                                  <label className="form-label">المكلف الافتراضي</label>
                                  <select className="form-select" value={step.default_assignee || ''} onChange={(e) => updateStep(si, k, { default_assignee: e.target.value })}>
                                    <option value="">— بدون —</option>
                                    {users.map((u) => <option key={u.id} value={u.email}>{u.name} ({u.email})</option>)}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label className="form-label">بند التسعير</label>
                                  <select className="form-select" value={step.pay_item_id || ''} onChange={(e) => updateStep(si, k, { pay_item_id: e.target.value })}>
                                    <option value="">— بلا تسعير —</option>
                                    {payItems.map((it) => (
                                      <option key={it.id} value={it.id}>{it.name}{it.unit ? ` / ${it.unit}` : ''}</option>
                                    ))}
                                  </select>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!step.requires_audio}
                                    onChange={(e) => updateStep(si, k, { requires_audio: e.target.checked })}
                                  />
                                  تشترط إرفاق صوت الدرس (mp3/m4a) قبل إغلاقها، ومنه يبدأ التفريغ آلياً
                                </label>
                              </>
                            )}

                            {step.type === 'approval' && (
                              <div className="form-group">
                                <label className="form-label">المعتمِد</label>
                                <select className="form-select" value={step.approver || ''} onChange={(e) => updateStep(si, k, { approver: e.target.value })}>
                                  <option value="">— اختر —</option>
                                  <option value="client">العميل</option>
                                  <option value="admin">المدير</option>
                                </select>
                              </div>
                            )}

                            {si > 0 && (
                              <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!step.deps}
                                    onChange={(e) => updateStep(si, k, { deps: e.target.checked ? deps : null })}
                                  />
                                  <span>متطلبات مخصّصة (بدل كل خطوات المرحلة السابقة)</span>
                                </label>

                                {step.deps && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                                    {earlier.map((e) => (
                                      <label key={e.uid} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                                        <input
                                          type="checkbox"
                                          checked={step.deps.includes(e.uid)}
                                          onChange={(ev) => updateStep(si, k, {
                                            deps: ev.target.checked
                                              ? [...step.deps, e.uid]
                                              : step.deps.filter((u) => u !== e.uid),
                                          })}
                                        />
                                        <span>{e.name || 'خطوة بلا اسم'}</span>
                                      </label>
                                    ))}
                                    {!earlier.length && <small style={{ color: 'var(--text-faint)' }}>لا توجد خطوات سابقة</small>}
                                  </div>
                                )}

                                {deps.length > 1 && (
                                  <div className="form-group" style={{ marginTop: 8, marginBottom: 0 }}>
                                    <label className="form-label">تبدأ الخطوة</label>
                                    <select className="form-select" value={step.join} onChange={(e) => updateStep(si, k, { join: e.target.value })}>
                                      <option value="all">بعد اكتمال كل متطلباتها ({deps.length})</option>
                                      <option value="any">بعد اكتمال أوّل متطلب</option>
                                    </select>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.name}>{saving ? 'جاري الحفظ...' : 'حفظ'}</button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
